import type { Role, UserPublic } from "../client"

export type RoleRequirement = {
  code?: string
  level?: string
}

const normalizeUpper = (value?: string | null) =>
  (value ?? "").trim().toUpperCase()

const normalizeLower = (value?: string | null) =>
  (value ?? "").trim().toLowerCase()

// Returns roles from user payload supporting both "roles" and legacy "role" keys.
export const getUserRoles = (user?: Partial<UserPublic> | null): Role[] => {
  const raw = (user as any)?.roles ?? (user as any)?.role
  if (!Array.isArray(raw)) return []
  return raw.filter(Boolean) as Role[]
}

// Checks if user has any role matching provided requirements (code and/or level).
export const hasRoleMatch = (
  user: Partial<UserPublic> | null | undefined,
  ...requirements: RoleRequirement[]
): boolean => {
  if (!user || requirements.length === 0) return false

  const roles = getUserRoles(user)
  if (roles.length === 0) return false

  return roles.some((role) =>
    requirements.some((req) => {
      const codeOk =
        !req.code || normalizeUpper(role.code) === normalizeUpper(req.code)
      const levelOk =
        !req.level || normalizeLower(role.level) === normalizeLower(req.level)
      return codeOk && levelOk
    }),
  )
}

export const isSuperAdmin = (user: Partial<UserPublic> | null | undefined) =>
  hasRoleMatch(user, { level: "superadmin" })

export const isAdminLevel = (user: Partial<UserPublic> | null | undefined) =>
  isSuperAdmin(user) || hasRoleMatch(user, { level: "admin" })

export const hasPermission = (
  user: Partial<UserPublic> | null | undefined,
  permissionCode: string,
): boolean => {
  if (!user) return false
  const roles = getUserRoles(user)
  return roles.some((role) => {
    // Superadmin override
    if (normalizeLower(role.level) === "superadmin") return true

    return (role.permissions ?? []).some(
      (p) => normalizeUpper(p.code) === normalizeUpper(permissionCode),
    )
  })
}
